/**
 * Project Feasibility Calculator - Multi-Metric Assessment
 *
 * Modular components:
 * - PentagonChart: Visual health metric display
 * - ScenarioSidebar: Detailed company stories
 * - Scenario data: Centralized scenario definitions
 */

import { ArrowLeft, Info } from "lucide-react";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { PageSEO } from "@/components/SEO/PageSEO";
import { PreparationPentagon } from "@/components/tools/PreparationPentagon";
import { RealityCheckPentagon } from "@/components/tools/RealityCheckPentagon";
import { ScenarioSidebar } from "@/components/tools/ScenarioSidebar";
import { scenarios, type Scenario } from "@/data/feasibilityScenarios";
import { analytics } from "@/utils/analytics";

interface RealityCheckMetrics {
  budgetGap: number;
  timelineGap: number;
  scopeRisk: number;
  changeImpact: number;
  alignmentScore: number;
  assessment:
    | "Proceed with Confidence"
    | "Addressable Challenges"
    | "Significant Obstacles"
    | "Major Rethinking Needed";
  recommendation: string;
}

interface PreparationMetrics {
  financialReadiness: number;
  scheduleReadiness: number;
  requirementsMaturity: number;
  technicalFoundation: number;
  organizationalReadiness: number;
  assessment:
    | "Well Prepared"
    | "Nearly Ready"
    | "Preparation Needed"
    | "Build Foundation First";
  recommendation: string;
}

const FeasibilityCalculator: React.FC = () => {
  const navigate = useNavigate();

  // Resources (0-100, higher = MORE resources)
  const [budgetAvailable, setBudgetAvailable] = useState(50);
  const [timeAvailable, setTimeAvailable] = useState(50);
  const [technicalComplexity, setTechnicalComplexity] = useState(50);
  const [requirementsClarity, setRequirementsClarity] = useState(50);
  const [changeReadiness, setChangeReadiness] = useState(50);

  // UI state
  const [selectedScenario, setSelectedScenario] = useState<Scenario | null>(
    null,
  );
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [realityCheckMetrics, setRealityCheckMetrics] =
    useState<RealityCheckMetrics | null>(null);
  const [preparationMetrics, setPreparationMetrics] =
    useState<PreparationMetrics | null>(null);
  const [activeModel, setActiveModel] = useState<"reality" | "preparation">(
    "reality",
  );

  // Calculate metrics
  useEffect(() => {
    calculateMetrics();
  }, [
    budgetAvailable,
    timeAvailable,
    technicalComplexity,
    requirementsClarity,
    changeReadiness,
  ]);

  const calculateMetrics = () => {
    // ========================================
    // REALITY CHECK MODEL (Problem-Focused)
    // ========================================
    const budgetGap = budgetAvailable - technicalComplexity;
    const timelineGap = timeAvailable - technicalComplexity;
    const scopeRisk = 100 - requirementsClarity;
    const changeImpact = Math.round(
      technicalComplexity * (1 - changeReadiness / 100),
    );
    const alignmentScore = Math.round(
      (budgetGap +
        50 +
        (timelineGap + 50) +
        requirementsClarity +
        changeReadiness) /
        4,
    );

    let realityAssessment: RealityCheckMetrics["assessment"];
    let realityRecommendation: string;

    // Gap >= 0 means you have enough resources (or a buffer)
    // Gap < 0 means you're short on resources
    if (budgetGap >= 0 && timelineGap >= 0 && scopeRisk < 30) {
      realityAssessment = "Proceed with Confidence";
      realityRecommendation =
        "Your plan looks solid with adequate resources and clear scope. Validate assumptions with our comprehensive assessment.";
    } else if (budgetGap >= -15 && timelineGap >= -15) {
      realityAssessment = "Addressable Challenges";
      realityRecommendation =
        "You have some gaps to address. Our assessment can help you identify specific areas to strengthen.";
    } else if (budgetGap >= -35 || timelineGap >= -35) {
      realityAssessment = "Significant Obstacles";
      realityRecommendation =
        "Your current plan has major gaps. Consider increasing resources or reducing scope. Let's explore your options together.";
    } else {
      realityAssessment = "Major Rethinking Needed";
      realityRecommendation =
        "This configuration needs fundamental changes. Our expert assessment will help you create a realistic plan.";
    }

    setRealityCheckMetrics({
      budgetGap: Math.max(-50, Math.min(50, budgetGap)),
      timelineGap: Math.max(-50, Math.min(50, timelineGap)),
      scopeRisk,
      changeImpact,
      alignmentScore,
      assessment: realityAssessment,
      recommendation: realityRecommendation,
    });

    // ========================================
    // PREPARATION MODEL (Solution-Focused)
    // ========================================
    const financialReadiness = Math.min(
      100,
      Math.round((budgetAvailable / Math.max(technicalComplexity, 1)) * 100),
    );
    const scheduleReadiness = Math.min(
      100,
      Math.round((timeAvailable / Math.max(technicalComplexity, 1)) * 100),
    );
    const requirementsMaturity = requirementsClarity;
    const technicalFoundation = Math.round(
      (financialReadiness + scheduleReadiness) / 2,
    );
    const organizationalReadiness = changeReadiness;

    const avgPreparation =
      (financialReadiness +
        scheduleReadiness +
        requirementsMaturity +
        organizationalReadiness) /
      4;

    let prepAssessment: PreparationMetrics["assessment"];
    let prepRecommendation: string;

    if (avgPreparation >= 70) {
      prepAssessment = "Well Prepared";
      prepRecommendation =
        "You're in great shape! Let's validate your preparation with our comprehensive assessment to ensure nothing is missed.";
    } else if (avgPreparation >= 50) {
      prepAssessment = "Nearly Ready";
      prepRecommendation =
        "You're on the right track. Our assessment will help you strengthen a few areas before you begin.";
    } else if (avgPreparation >= 30) {
      prepAssessment = "Preparation Needed";
      prepRecommendation =
        "You'll benefit from more planning. Our assessment identifies exactly what you need to get ready.";
    } else {
      prepAssessment = "Build Foundation First";
      prepRecommendation =
        "Let's build a solid foundation together. Our expert assessment will guide you through the preparation process.";
    }

    setPreparationMetrics({
      financialReadiness,
      scheduleReadiness,
      requirementsMaturity,
      technicalFoundation,
      organizationalReadiness,
      assessment: prepAssessment,
      recommendation: prepRecommendation,
    });

    analytics.trackToolUsage("Feasibility Calculator", "Metrics Calculated", {
      budgetAvailable,
      timeAvailable,
      technicalComplexity,
      requirementsClarity,
      changeReadiness,
      realityAssessment,
      prepAssessment,
    });
  };

  const loadScenario = (scenario: Scenario) => {
    setSelectedScenario(scenario);
    setBudgetAvailable(scenario.budgetAvailable);
    setTimeAvailable(scenario.timeAvailable);
    setTechnicalComplexity(scenario.technicalComplexity);
    setRequirementsClarity(scenario.requirementsClarity || 50);
    setChangeReadiness(scenario.changeReadiness || 50);
    setSidebarOpen(true);

    analytics.trackToolUsage("Feasibility Calculator", "Scenario Selected", {
      scenarioId: scenario.id,
      scenarioName: scenario.name,
    });
  };

  const getSuccessColor = (value: number): string => {
    if (value >= 75) return "#10b981";
    if (value >= 50) return "#f59e0b";
    if (value >= 25) return "#f97316";
    return "#ef4444";
  };

  // Tooltip component
  const Tooltip = ({
    children,
    content,
  }: {
    children: React.ReactNode;
    content: string;
  }) => {
    const [show, setShow] = useState(false);

    return (
      <div className="relative inline-block">
        <button
          type="button"
          onMouseEnter={() => setShow(true)}
          onMouseLeave={() => setShow(false)}
          onClick={() => setShow(!show)}
          className="text-slate-400 hover:text-[#B87333] transition-colors"
        >
          {children}
        </button>
        {show && (
          <div className="absolute z-50 w-80 p-4 bg-slate-900 text-white text-sm rounded-lg shadow-2xl -top-2 left-8 pointer-events-none">
            <div className="whitespace-pre-line leading-relaxed">{content}</div>
            <div className="absolute w-3 h-3 bg-slate-900 transform rotate-45 -left-1.5 top-4" />
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <PageSEO
        title="Project Feasibility Calculator"
        description="Evaluate your project's success probability, difficulty, and risk factors. Interactive multi-metric assessment for AI automation and custom software projects."
        keywords="project feasibility, success probability, project risk assessment, complexity analysis, resource planning, project metrics"
        ogImage="/og-image-tools.png"
      />

      <div className="flex flex-col min-h-screen bg-slate-50">
        {/* Header Section */}
        <div className="bg-slate-50 border-b border-slate-200">
          <div className="w-full px-4 sm:px-6 lg:px-10 py-8 lg:py-12">
            {/* Header */}
            <div className="mb-6 lg:mb-8">
              <button
                onClick={() => navigate("/tools")}
                className="flex items-center gap-2 text-slate-600 hover:text-[#B87333] mb-4 lg:mb-6 transition-colors group"
              >
                <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                <span className="font-semibold">Back to Tools</span>
              </button>

              <div className="text-center mb-6 lg:mb-8">
                <p className="text-xs sm:text-sm uppercase tracking-widest text-slate-500 font-semibold mb-3 lg:mb-4">
                  Multi-Metric Project Assessment
                </p>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-slate-900 leading-tight mb-4 lg:mb-6">
                  Project Feasibility Calculator
                </h1>
                <p className="text-base sm:text-lg lg:text-xl text-slate-600 max-w-[800px] mx-auto">
                  Understand your project's success probability, difficulty, and
                  risk factors. Resources enable complexity—see how they
                  interact.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Area with Flexing Sidebar */}
        <div className="flex flex-1 overflow-hidden relative">
          {/* Main Content - Flexes when sidebar opens */}
          <div
            className={`
              flex-1
              overflow-auto
              transition-all
              duration-300
              ${sidebarOpen ? "lg:mr-[480px]" : ""}
            `}
          >
            <div className="w-full px-4 sm:px-6 lg:px-10 py-6 lg:py-12">
              {/* Content Container */}
              <div>
                {/* Scenario Quick-Select */}
                <div className="mb-6 lg:mb-8">
                  <h2 className="text-xl lg:text-2xl font-bold text-slate-900 mb-2">
                    Common Scenarios
                  </h2>
                  <p className="text-slate-600 mb-3 lg:mb-4 text-sm">
                    Select a scenario to see detailed analysis →
                  </p>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    {scenarios.map((scenario) => (
                      <button
                        key={scenario.id}
                        onClick={() => loadScenario(scenario)}
                        className={`p-3 rounded-lg border-2 transition-all duration-300 text-left ${
                          selectedScenario?.id === scenario.id
                            ? "border-[#B87333] bg-[#B87333]/5 shadow-md"
                            : "border-slate-200 hover:border-[#B87333]/50 hover:shadow-sm bg-white"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <div
                            className={`p-1.5 rounded ${
                              selectedScenario?.id === scenario.id
                                ? "bg-[#B87333] text-white"
                                : "bg-slate-100 text-slate-600"
                            }`}
                          >
                            {scenario.icon}
                          </div>
                          <h3 className="font-bold text-sm text-slate-900">
                            {scenario.name}
                          </h3>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Two-Column Layout: Sliders + Pentagon */}
                <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 mb-8 lg:mb-12">
                  {/* LEFT COLUMN: Controls */}
                  <div className="contents lg:block lg:flex-1 lg:min-w-0 lg:space-y-6">
                    {/* Sliders */}
                    <div className="order-2 lg:!order-none bg-white rounded-2xl shadow-xl p-8">
                      <h2 className="text-2xl font-bold text-slate-900 mb-2">
                        Adjust Your Project
                      </h2>
                      <p className="text-sm text-slate-600 mb-6">
                        Move the sliders to match your project's situation.
                      </p>

                      <div className="space-y-6">
                        {/* Budget */}
                        <div>
                          <div className="flex justify-between items-center mb-3">
                            <div className="flex items-center gap-2">
                              <label className="text-sm font-semibold text-slate-700">
                                Budget Resources
                              </label>
                              <Tooltip
                                content={`💰 Budget & Complexity Fit\n\nAdequate budget enables quality development\n\n• Ample (75+): Senior developers, proper architecture\n• Adequate (50-75): Solid mid-level team\n• Limited (25-50): May need scope adjustment\n• Very limited (0-25): Consider increasing budget\n\nMoving RIGHT = MORE budget = higher quality team`}
                              >
                                <Info className="w-4 h-4" />
                              </Tooltip>
                            </div>
                            <span
                              className="text-sm font-bold"
                              style={{
                                color: getSuccessColor(budgetAvailable),
                              }}
                            >
                              {budgetAvailable > 75
                                ? "Ample"
                                : budgetAvailable > 50
                                  ? "Adequate"
                                  : budgetAvailable > 25
                                    ? "Limited"
                                    : "Very Limited"}
                            </span>
                          </div>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={budgetAvailable}
                            onChange={(e) =>
                              setBudgetAvailable(Number(e.target.value))
                            }
                            className="w-full h-3 bg-slate-200 rounded-lg appearance-none cursor-pointer"
                            style={{
                              accentColor: getSuccessColor(budgetAvailable),
                            }}
                          />
                        </div>

                        {/* Timeline */}
                        <div>
                          <div className="flex justify-between items-center mb-3">
                            <div className="flex items-center gap-2">
                              <label className="text-sm font-semibold text-slate-700">
                                Timeline Resources
                              </label>
                              <Tooltip
                                content={`⏰ Timeline & Complexity Fit\n\nAdequate time allows proper planning and quality work\n\n• Flexible (75+): Proper planning, refactoring\n• Reasonable (50-75): Adequate for most projects\n• Tight (25-50): May need scope adjustment\n• Very tight (0-25): Consider extending timeline\n\nMoving RIGHT = MORE time = better quality outcomes`}
                              >
                                <Info className="w-4 h-4" />
                              </Tooltip>
                            </div>
                            <span
                              className="text-sm font-bold"
                              style={{ color: getSuccessColor(timeAvailable) }}
                            >
                              {timeAvailable > 75
                                ? "Flexible"
                                : timeAvailable > 50
                                  ? "Reasonable"
                                  : timeAvailable > 25
                                    ? "Tight"
                                    : "Very Tight"}
                            </span>
                          </div>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={timeAvailable}
                            onChange={(e) =>
                              setTimeAvailable(Number(e.target.value))
                            }
                            className="w-full h-3 bg-slate-200 rounded-lg appearance-none cursor-pointer"
                            style={{
                              accentColor: getSuccessColor(timeAvailable),
                            }}
                          />
                        </div>

                        {/* Complexity */}
                        <div>
                          <div className="flex justify-between items-center mb-3">
                            <div className="flex items-center gap-2">
                              <label className="text-sm font-semibold text-slate-700">
                                Technical Complexity
                              </label>
                              <Tooltip
                                content={`🧩 Complexity Demands Resources\n\nHigher complexity needs more time + budget\n\n• Very high (75+): Multiple integrations, legacy systems\n• High (50-75): Several APIs, complex logic\n• Moderate (25-50): Standard CRUD + integrations\n• Low (0-25): Simple workflows\n\nMoving RIGHT = MORE complex = needs more resources`}
                              >
                                <Info className="w-4 h-4" />
                              </Tooltip>
                            </div>
                            <span className="text-sm font-bold text-slate-700">
                              {technicalComplexity > 75
                                ? "Very High"
                                : technicalComplexity > 50
                                  ? "High"
                                  : technicalComplexity > 25
                                    ? "Moderate"
                                    : "Low"}
                            </span>
                          </div>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={technicalComplexity}
                            onChange={(e) =>
                              setTechnicalComplexity(Number(e.target.value))
                            }
                            className="w-full h-3 bg-slate-200 rounded-lg appearance-none cursor-pointer"
                            style={{ accentColor: "#64748b" }}
                          />
                        </div>

                        {/* Requirements Clarity */}
                        <div>
                          <div className="flex justify-between items-center mb-3">
                            <div className="flex items-center gap-2">
                              <label className="text-sm font-semibold text-slate-700">
                                Requirements Clarity
                              </label>
                              <Tooltip
                                content={`📋 How Well-Defined Is Your Vision?\n\nClear requirements reduce scope changes\n\n• Crystal clear (75+): Detailed specs, user flows mapped\n• Well defined (50-75): Core features understood\n• Somewhat clear (25-50): General direction, needs refinement\n• Exploratory (0-25): Still discovering what you need\n\nMoving RIGHT = MORE clarity = fewer surprises`}
                              >
                                <Info className="w-4 h-4" />
                              </Tooltip>
                            </div>
                            <span
                              className="text-sm font-bold"
                              style={{
                                color: getSuccessColor(requirementsClarity),
                              }}
                            >
                              {requirementsClarity > 75
                                ? "Crystal Clear"
                                : requirementsClarity > 50
                                  ? "Well Defined"
                                  : requirementsClarity > 25
                                    ? "Somewhat Clear"
                                    : "Exploratory"}
                            </span>
                          </div>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={requirementsClarity}
                            onChange={(e) =>
                              setRequirementsClarity(Number(e.target.value))
                            }
                            className="w-full h-3 bg-slate-200 rounded-lg appearance-none cursor-pointer"
                            style={{
                              accentColor: getSuccessColor(requirementsClarity),
                            }}
                          />
                        </div>

                        {/* Change Readiness */}
                        <div>
                          <div className="flex justify-between items-center mb-3">
                            <div className="flex items-center gap-2">
                              <label className="text-sm font-semibold text-slate-700">
                                Change Readiness
                              </label>
                              <Tooltip
                                content={`🔄 How Ready Is Your Team?\n\nOrganizational change readiness matters\n\n• Highly ready (75+): Team excited, leadership aligned\n• Ready (50-75): Generally supportive\n• Cautious (25-50): Some resistance expected\n• Resistant (0-25): Significant change management needed\n\nMoving RIGHT = MORE readiness = smoother adoption`}
                              >
                                <Info className="w-4 h-4" />
                              </Tooltip>
                            </div>
                            <span
                              className="text-sm font-bold"
                              style={{
                                color: getSuccessColor(changeReadiness),
                              }}
                            >
                              {changeReadiness > 75
                                ? "Highly Ready"
                                : changeReadiness > 50
                                  ? "Ready"
                                  : changeReadiness > 25
                                    ? "Cautious"
                                    : "Resistant"}
                            </span>
                          </div>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={changeReadiness}
                            onChange={(e) =>
                              setChangeReadiness(Number(e.target.value))
                            }
                            className="w-full h-3 bg-slate-200 rounded-lg appearance-none cursor-pointer"
                            style={{
                              accentColor: getSuccessColor(changeReadiness),
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Assessment Result */}
                    {((activeModel === "reality" && realityCheckMetrics) ||
                      (activeModel === "preparation" &&
                        preparationMetrics)) && (
                      <div className="order-3 lg:!order-none bg-white rounded-xl p-6 shadow-lg border-2 border-slate-200">
                        <h3 className="text-lg font-bold text-slate-900 mb-1">
                          {activeModel === "reality"
                            ? `Reality Check: ${realityCheckMetrics?.assessment}`
                            : `Preparation: ${preparationMetrics?.assessment}`}
                        </h3>
                        <p className="text-sm text-slate-600 mb-4">
                          {activeModel === "reality"
                            ? realityCheckMetrics?.recommendation
                            : preparationMetrics?.recommendation}
                        </p>
                        <button
                          onClick={() => {
                            analytics.conversion("assessment_started", 1, {
                              source: `feasibility_calculator_${activeModel}_cta`,
                              assessment:
                                activeModel === "reality"
                                  ? realityCheckMetrics?.assessment
                                  : preparationMetrics?.assessment,
                              page: "/tools/feasibility-calculator",
                            });
                            window.location.href =
                              "/tools/readiness-assessment";
                          }}
                          className={`w-full px-4 py-3 rounded-lg font-semibold text-sm transition-all duration-300 shadow-md hover:shadow-lg text-white ${
                            activeModel === "reality"
                              ? "bg-[#B87333] hover:bg-[#A67329]"
                              : "bg-blue-600 hover:bg-blue-700"
                          }`}
                        >
                          Get Detailed Assessment →
                        </button>
                      </div>
                    )}
                  </div>

                  {/* RIGHT COLUMN: Pentagon Display with Integrated Toggle */}
                  <div className="contents lg:block lg:flex-1 lg:min-w-0 lg:space-y-6">
                    {/* Pentagon Display with Model Toggle */}
                    <div
                      className={`order-1 lg:!order-none bg-white rounded-2xl shadow-xl p-2 sm:p-6 border-2 transition-colors duration-300 ${
                        activeModel === "reality"
                          ? "border-[#B87333]"
                          : "border-blue-600"
                      }`}
                    >
                      {/* Model Toggle */}
                      <div className="mb-6">
                        <h3 className="text-lg font-bold text-slate-900 mb-3">
                          Choose Your Perspective
                        </h3>
                        <p className="text-xs text-slate-600 mb-4">
                          Our assessment, problem identification, and solution
                          architecture services help you understand what
                          benefits you could gain from implementing new systems,
                          software, and AI solutions.
                        </p>
                        <div className="flex gap-3">
                          <button
                            onClick={() => setActiveModel("reality")}
                            className={`flex-1 px-4 py-3 rounded-lg font-semibold text-sm transition-all duration-300 ${
                              activeModel === "reality"
                                ? "bg-[#B87333] text-white shadow-lg"
                                : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                            }`}
                          >
                            <div className="text-lg mb-1">❓</div>
                            Reality Check
                            <div className="text-xs font-normal mt-1">
                              Questioning commitment?
                            </div>
                          </button>
                          <button
                            onClick={() => setActiveModel("preparation")}
                            className={`flex-1 px-4 py-3 rounded-lg font-semibold text-sm transition-all duration-300 ${
                              activeModel === "preparation"
                                ? "bg-blue-600 text-white shadow-lg"
                                : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                            }`}
                          >
                            <div className="text-lg mb-1">✅</div>
                            Preparation
                            <div className="text-xs font-normal mt-1">
                              Ready to build?
                            </div>
                          </button>
                        </div>
                      </div>

                      {/* Pentagon Display */}
                      {activeModel === "reality" && realityCheckMetrics && (
                        <RealityCheckPentagon metrics={realityCheckMetrics} />
                      )}
                      {activeModel === "preparation" && preparationMetrics && (
                        <PreparationPentagon metrics={preparationMetrics} />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Collapsible Sidebar */}
          <ScenarioSidebar
            scenario={selectedScenario}
            isOpen={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
          />
        </div>
      </div>
    </>
  );
};

export default FeasibilityCalculator;
